import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ScanLine, X, Check, Delete, Search, Lock } from "lucide-react";
import { api, errMsg, fmtNum, fmtDate } from "@/lib/api";
import { enqueueCount, isNetworkError, cacheBatch, cachedBatch } from "@/lib/opnameQueue";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { beep } from "./shared";

export function OpnameCounter({ session, onCounted, onQueued }) {
  const [code, setCode] = useState("");
  const [current, setCurrent] = useState(null);
  const [qty, setQty] = useState("");
  const [cam, setCam] = useState(false);
  const [pick, setPick] = useState("");
  const [options, setOptions] = useState([]);
  const scannerRef = useRef(null);
  const qtyRef = useRef(null);

  const lookup = useCallback(async (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    const m = t.match(/\/batch\/([^/?#\s]+)/i);
    const c = m ? decodeURIComponent(m[1]) : t;
    try {
      const r = await api.get(`/inventory/by-code/${encodeURIComponent(c)}`);
      cacheBatch(r.data);
      beep(true); setCurrent(r.data); setQty(""); setTimeout(() => qtyRef.current?.focus(), 50);
    } catch (e) {
      const cached = isNetworkError(e) ? cachedBatch(c) : null;
      if (cached) { beep(true); setCurrent({ ...cached, _offline: true }); setQty(""); toast.warning("Offline — memakai data batch tersimpan"); setTimeout(() => qtyRef.current?.focus(), 50); }
      else { beep(false); toast.error(isNetworkError(e) ? "Tidak ada koneksi dan batch belum pernah dimuat" : errMsg(e)); }
    }
    setCode("");
  }, []);

  useEffect(() => {
    if (!cam) { scannerRef.current?.stop().catch(() => {}); scannerRef.current = null; return; }
    let stopped = false;
    import("html5-qrcode").then(({ Html5Qrcode }) => {
      if (stopped) return;
      const s = new Html5Qrcode("opname-camera");
      scannerRef.current = s;
      s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => { if (!current) lookup(text); }, () => {}).catch((e) => { toast.error("Kamera tidak tersedia: " + (e?.message || e)); setCam(false); });
    });
    return () => { stopped = true; scannerRef.current?.stop().catch(() => {}); scannerRef.current = null; };
  }, [cam, lookup, current]);

  useEffect(() => {
    if (pick.trim().length < 2) { setOptions([]); return; }
    const t = setTimeout(() => api.get("/inventory", { params: { search: pick.trim() } }).then((r) => { r.data.forEach(cacheBatch); setOptions(r.data.slice(0, 8)); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [pick]);

  const save = async () => {
    const q = Number(qty);
    if (!current || !(q >= 0) || qty === "") return;
    const queueIt = () => { enqueueCount(session.id, current, q); beep(true); toast.warning(`Tidak ada sinyal — hitungan ${current.batch_code} disimpan di antrian offline`); onQueued?.(); setCurrent(null); setQty(""); };
    if (!navigator.onLine) return queueIt();
    try {
      const r = await api.post(`/opname/sessions/${session.id}/count`, { inventory_id: current.id, physical_qty: q });
      beep(true); toast.success(`${current.batch_code}: fisik ${fmtNum(q)} ${current.unit || "MC"} tersimpan`);
      onCounted(r.data); setCurrent(null); setQty("");
    } catch (e) { if (isNetworkError(e)) queueIt(); else { beep(false); toast.error(errMsg(e)); } }
  };
  const key = (k) => { if (k === "del") setQty((q) => q.slice(0, -1)); else if (k === ",") { if (current?.unit === "KG" && !qty.includes(".")) setQty((q) => (q || "0") + "."); } else setQty((q) => (q === "0" ? k : q + k)); };

  return (
    <div className="space-y-3" data-testid="opname-counter">
      <div className="card-aqua p-3">
        <form onSubmit={(e) => { e.preventDefault(); lookup(code); }} className="flex gap-2">
          <div className="relative flex-1"><ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-sky-600" /><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan / ketik kode batch lalu Enter" className="pl-10 h-11 font-mono" data-testid="input-opname-scan" /></div>
          <Button type="button" variant={cam ? "default" : "outline"} className={`h-11 rounded-xl ${cam ? "bg-sky-600" : "border-sky-300 text-sky-700"}`} onClick={() => setCam((c) => !c)} data-testid="btn-opname-camera"><Camera className="h-5 w-5" /></Button>
        </form>
        {cam && <div id="opname-camera" className="mt-3 rounded-xl overflow-hidden bg-black" />}
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={pick} onChange={(e) => setPick(e.target.value)} placeholder="Atau cari dari daftar: kode / jenis ikan..." className="pl-9 h-10" data-testid="input-opname-pick" />
          {options.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full bg-white border border-sky-100 rounded-xl shadow-lg max-h-64 overflow-auto text-sm" data-testid="opname-pick-options">
              {options.map((o, i) => <li key={o.id}><button type="button" onClick={() => { setCurrent(o); setQty(""); setPick(""); setOptions([]); setTimeout(() => qtyRef.current?.focus(), 50); }} className="w-full text-left px-3 py-2 hover:bg-sky-50 flex justify-between gap-2" data-testid={`opname-pick-${i}`}><span><span className="font-mono text-xs font-bold text-sky-800">{o.batch_code}</span> {o.species_name} {o.size}</span><span className="text-xs text-slate-400">{o.location}</span></button></li>)}
            </ul>
          )}
        </div>
      </div>
      {current && (
        <div className="card-aqua p-4 border-2 border-sky-300" data-testid="opname-current">
          <div className="flex items-start justify-between">
            <div><div className="font-mono font-extrabold text-sky-800 text-lg" data-testid="opname-current-code">{current.batch_code}</div><div className="font-bold text-slate-900">{current.species_name} <span className="font-normal text-slate-600">{current.size}</span></div><div className="text-xs text-slate-500">{current.location} • masuk {fmtDate(current.entry_date)} • satuan <b className="text-slate-800">{current.unit || "MC"}</b> <span className="inline-flex items-center gap-0.5 text-slate-400" data-testid="opname-current-blind"><Lock className="h-3 w-3" /> stok sistem disembunyikan</span></div></div>
            <button type="button" onClick={() => setCurrent(null)} className="p-1 text-slate-400 hover:text-rose-600" data-testid="btn-opname-cancel-current"><X className="h-5 w-5" /></button>
          </div>
          <Input ref={qtyRef} value={qty} onChange={(e) => setQty(e.target.value.replace(",", ".").replace(/[^\d.]/g, ""))} onKeyDown={(e) => e.key === "Enter" && save()} inputMode="decimal" placeholder={`Jumlah fisik (${current.unit || "MC"})`} className="mt-3 h-14 text-2xl font-mono text-center" data-testid="input-opname-qty" />
          <div className="grid grid-cols-3 gap-2 mt-2 sm:hidden">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "del"].map((k) => <button key={k} type="button" onClick={() => key(k)} className={`h-12 rounded-xl text-xl font-bold ${k === "del" ? "bg-rose-50 text-rose-600" : k === "," ? "bg-slate-100 text-slate-500" : "bg-sky-50 text-sky-900"}`} data-testid={`opname-key-${k}`}>{k === "del" ? <Delete className="h-5 w-5 mx-auto" /> : k}</button>)}
          </div>
          <Button type="button" onClick={save} disabled={qty === ""} className="w-full h-12 mt-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-base" data-testid="btn-opname-save-count"><Check className="h-5 w-5 mr-1" /> Simpan hitungan</Button>
        </div>
      )}
    </div>
  );
}
